<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { onClickOutside } from '@vueuse/core';
import { useEventListener } from '@vueuse/core';
import Tiptap, { TipTapExposedMethods } from './ConnxTipTapEditor.vue';
import Toolbar from './ConnxTipTapToolbar.vue';
import CodeMirror, { type CodeMirrorExposedMethods } from './ConnxCodeMirrorEditor.vue';
import beautify from 'js-beautify';
import { useScrollLock } from '@vueuse/core';
import { collapseElement } from '@/lib/collapse-element';

const content = defineModel<string>({ required: true });
const isLocked = useScrollLock(document.body);

const props = defineProps<{
  expanded?: boolean;
  maxHeight?: string; //tailwind class e.g. tw-max-h-92
  disabled?: boolean;
}>();

const showRawHtml = ref(false);
const isFullscreen = ref(false);
const tipTapContainerRef = ref();
const codemirrorRef = ref<CodeMirrorExposedMethods | null>(null);
const tiptapRef = ref<TipTapExposedMethods | null>(null);
const mainComponentRef = ref();

const emit = defineEmits(['update:expanded']);

const focus = () => {
  if (showRawHtml.value) {
    codemirrorRef.value?.focus();
    return;
  }

  tiptapRef.value?.focus();
};

defineExpose({
  focus,
});

const isExpanded = computed({
  get() {
    return props.expanded;
  },
  set(newValue) {
    if (newValue === false) {
      collapseElement(tipTapContainerRef.value).then(() => {
        emit('update:expanded', newValue);
      });
    } else {
      emit('update:expanded', newValue);
    }
  },
});

const onEditorFocus = () => {
  isExpanded.value = true;
};

const onEditorBlur = () => {
  if (tipTapContainerRef.value) {
    if (isFullscreen.value) {
      isExpanded.value = false;
    } else {
      isExpanded.value = false;
      collapseElement(tipTapContainerRef.value).then(() => {});
    }
  }
};

const handleAction = (action: string, payload?: any) => {
  if (action === 'toggleRawHtml') {
    showRawHtml.value = !showRawHtml.value;

    // Sync content when switching modes
    if (showRawHtml.value) {
      // Switching to raw HTML - get content from Tiptap
      content.value = beautify.html(tiptapRef.value?.getContent() || '', {
        indent_size: 2,
      });
    } else {
      // Switching back to WYSIWYG - get content from CodeMirror
      content.value = codemirrorRef.value?.getContent() || '';
    }
    return;
  }

  if (action === 'toggleFullscreen') {
    isFullscreen.value = !isFullscreen.value;
    focus();
    return;
  }

  tiptapRef.value?.handleAction(action, payload);
};

const isActive = (type: string) => {
  return tiptapRef.value?.isActive(type) ?? false;
};

useEventListener(mainComponentRef, 'keyup', (event: KeyboardEvent) => {
  if (event.key === 'Escape' && isFullscreen.value) {
    isFullscreen.value = false;
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  }
});

onClickOutside(mainComponentRef, () => {
  if (isExpanded.value) {
    onEditorBlur();
  }
});

watch(isFullscreen, (val) => {
  isLocked.value = val;
});

watch(
  () => props.disabled,
  (newVal) => {
    if (newVal) {
      showRawHtml.value = false;
      isFullscreen.value = false;
    }
  }
);
</script>

<template>
  <div
    ref="mainComponentRef"
    class="tw-space-y-2"
    :class="{
      'tw-fixed tw-inset-0 tw-left-0 tw-right-0 tw-z-1200 tw-bg-white tw-p-4 tw-flex tw-justify-center tw-items-center':
        isFullscreen,
      'tw-relative': !isFullscreen,
    }"
  >
    <div
      class="tw-bg-connx-white tw-rounded tw-w-full"
      :class="{
        'tw-p-4': isFullscreen,
      }"
    >
      <Toolbar
        :disabled="disabled"
        :show-raw-html="showRawHtml"
        :is-active="isActive"
        :is-fullscreen="isFullscreen"
        @action="handleAction"
      >
        <slot name="toolbar"></slot>
      </Toolbar>

      <div
        class="Field Field--editor tw-p-0 tw-mt-2"
        :class="{
          'ScrollBar ScrollBar--vertical': isFullscreen,
        }"
      >
        <div
          ref="tipTapContainerRef"
          class="tw-overflow-hidden tw-min-h-32 tw-transition-[height] tw-duration-500 tw-ease-in-out"
          :class="{
            'tw-h-[calc(100dvh_-_85px)] tw-bg-connx-white': isFullscreen,
            'tw-max-h-none': isExpanded && !isFullscreen && !maxHeight,
            'tw-max-h-32': !isExpanded && !isFullscreen && !maxHeight,
            maxHeight: maxHeight,
            'tw-bg-connx-grey-tint-85': disabled,
          }"
        >
          <Tiptap
            v-if="!showRawHtml"
            ref="tiptapRef"
            v-model="content"
            :disabled="disabled"
            :is-fullscreen="isFullscreen"
            @focus="onEditorFocus"
            @blur="onEditorBlur"
          />

          <CodeMirror
            v-else
            ref="codemirrorRef"
            v-model="content"
            :disabled="disabled"
            :is-fullscreen="isFullscreen"
            @focus="onEditorFocus"
            @blur="onEditorBlur"
          />
        </div>
      </div>
    </div>
  </div>
</template>
